import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Adds a single variant to the cart without leaving the page, then lets every
 * cart-items-component (drawer and cart page) re-render from the returned sections.
 * Wraps a regular /cart/add form, so it still works as a plain form post without JS.
 *
 * @extends {HTMLElement}
 */
class CartQuickAdd extends HTMLElement {
  connectedCallback() {
    this.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#handleSubmit);
  }

  get form() {
    return this.querySelector('form');
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = this.form;
    const button = form?.querySelector('[type="submit"]');
    if (!form || !button || button.getAttribute('aria-disabled') === 'true') return;

    button.setAttribute('aria-disabled', 'true');
    this.#setError('');

    const formData = new FormData(form);
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);
    if (sectionIds.length > 0) formData.append('sections', sectionIds.join(','));

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        window.dispatchEvent(new CartErrorEvent(this.id, data.message));
        this.#setError(data.message);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, String(formData.get('id')), {
          source: 'cart-quick-add',
          itemCount: Number(formData.get('quantity')) || 1,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#setError('Something went wrong. Please try again.');
    } finally {
      button.removeAttribute('aria-disabled');
    }
  };

  /**
   * @param {string} message
   */
  #setError(message) {
    const errorElement = this.querySelector('[data-quick-add-error]');
    if (errorElement) errorElement.textContent = message;
  }
}

if (!customElements.get('cart-quick-add')) {
  customElements.define('cart-quick-add', CartQuickAdd);
}
